import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, Circle, Loader2, ListTodo, Flame, CalendarDays, CalendarClock } from 'lucide-react';
import TaskTimeline from './TaskTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQ_META = {
  daily:   { label: 'Daily Tasks',   icon: Flame,         color: 'text-rose-500'   },
  weekly:  { label: 'Weekly Tasks',  icon: CalendarDays,  color: 'text-amber-500'  },
  monthly: { label: 'Monthly Tasks', icon: CalendarClock, color: 'text-blue-500'   },
  once:    { label: 'One-off Tasks', icon: ListTodo,      color: 'text-emerald-500'},
};

const PRIORITY_BADGE = {
  high:   'bg-rose-500/15 text-rose-600 border-rose-500/40',
  medium: 'bg-amber-500/15 text-amber-600 border-amber-500/40',
  low:    'bg-slate-500/15 text-slate-600 border-slate-500/40',
};

/**
 * Task list for the logged-in clerk. Grouped by frequency.
 * Clerks can toggle pending → in_progress → done.
 */
export default function ClerkTasksWidget() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clerk-tasks`, { headers });
      setTasks(res.data || []);
    } catch {}
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const cycleStatus = async (task) => {
    const order = ['pending', 'in_progress', 'done'];
    const idx = order.indexOf(task.status);
    const next = order[(idx + 1) % order.length];
    try {
      await axios.put(`${API_URL}/api/clerk-tasks/${task.id}`, { status: next }, { headers });
      if (next === 'done') toast.success('Task complete — nice work!');
      fetchTasks();
    } catch {
      toast.error('Update failed');
    }
  };

  // Group by frequency
  const grouped = { daily: [], weekly: [], monthly: [], once: [] };
  for (const t of tasks) {
    const f = FREQ_META[t.frequency] ? t.frequency : 'once';
    grouped[f].push(t);
  }

  const StatusIcon = ({ status }) => {
    if (status === 'done') return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
    if (status === 'in_progress') return <Loader2 className="h-5 w-5 text-amber-500 animate-spin" />;
    return <Circle className="h-5 w-5 text-slate-400" />;
  };

  return (
    <Card className="mb-4" data-testid="clerk-tasks-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-blue-500" />
            <h3 className="font-semibold">My Tasks</h3>
            <span className="text-xs text-muted-foreground">({tasks.length})</span>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-3">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-5 border border-dashed rounded-lg">
            No tasks assigned yet. Nothing to do — enjoy your day!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(grouped).map(([freq, items]) => {
              if (items.length === 0) return null;
              const meta = FREQ_META[freq];
              const GIcon = meta.icon;
              return (
                <div key={freq} className="border rounded-lg p-2">
                  <div className="flex items-center gap-1.5 px-1 pb-2 mb-1 border-b">
                    <GIcon className={`h-4 w-4 ${meta.color}`} />
                    <span className="font-semibold text-sm">{meta.label}</span>
                    <span className="text-xs text-muted-foreground">({items.length})</span>
                  </div>
                  <div className="space-y-1.5">
                    {items.map(t => (
                      <div
                        key={t.id}
                        className={`flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 ${t.status === 'done' ? 'opacity-60' : ''}`}
                        data-testid={`task-${t.id}`}
                      >
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 flex-shrink-0"
                          onClick={() => cycleStatus(t)}
                          title="Click to cycle status"
                          data-testid={`task-toggle-${t.id}`}
                        >
                          <StatusIcon status={t.status} />
                        </Button>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-medium leading-tight ${t.status === 'done' ? 'line-through' : ''}`}>
                            {t.title}
                          </div>
                          {t.description && (
                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</div>
                          )}
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {t.priority && (
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${PRIORITY_BADGE[t.priority] || ''}`}>
                                {t.priority}
                              </Badge>
                            )}
                            {t.is_due_today && t.status !== 'done' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-indigo-500/15 text-indigo-600 border-indigo-500/40">
                                Due today
                              </Badge>
                            )}
                          </div>
                          <TaskTimeline task={t} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
